import { map, get, kebabCase } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import CardCarousel from 'components/CardCarousel';
import { Container, Grid, Cell } from 'components/Grid';
import { PrimaryHeading } from 'components/Headings';
import Image from 'components/ImageV2';
import { Small, MediumUp } from 'components/MediaQuery';
import RichText from 'components/RichText';
import SustainabilityPageCopy from 'components/SustainabilityPageCopy';
import SustainabilityPageShowcase from 'components/SustainabilityPageShowcase';
import { IMAGE_RATIOS, IMAGE_WIDTHS } from 'constants/images';
import { mediumUp, largeUp } from 'constants/mediaQueries';
import { PAGE_SECTIONS } from 'constants/sustainabilityPage';
import CardCarouselModel from 'models/CardCarousel';
import Theme from 'styles/theme';

import PageSectionPlaceholder from './PageSectionPlaceholder';

const BORDER_RADIUS = Theme.get('spacing') * 5;

class SustainabilityPageSlide extends React.PureComponent {
  renderPageSection = data => {
    const type = get(data, '_contentType');
    const { trackEvent } = this.props;

    switch (type) {
      case PAGE_SECTIONS.SHOWCASE:
        return <SustainabilityPageShowcase data={data} trackEvent={trackEvent} />;
      case PAGE_SECTIONS.CARD_CAROUSEL:
        return (
          <div className="SustainabilityPageSlide__page-section__card-carousel">
            <Container>
              <Grid isCentered className="SustainabilityPageSlide__carousel-content">
                <Cell
                  smallUp={{ columns: 12 }}
                  mediumUp={{ columns: 11 }}
                  largeUp={{ columns: 10, offset: 2 }}
                >
                  <CardCarousel data={new CardCarouselModel(data)} trackEvent={trackEvent} />
                </Cell>
              </Grid>
            </Container>
            <div className="SustainabilityPageSlide__page-section__card-carousel--overlay" />
          </div>
        );
      case PAGE_SECTIONS.COPY:
        return <SustainabilityPageCopy data={data} />;
      default:
        return <PageSectionPlaceholder data={data} trackEvent={trackEvent} />;
    }
  };

  render() {
    const { data, zIndex, forwardedRef } = this.props;

    const heroImageDesktop = get(data, ['heroImage']);
    const heroImageMobile = get(data, ['heroImageMobile']) || heroImageDesktop;
    const label = get(data, 'navigationLabel');

    return (
      <>
        <div
          ref={forwardedRef}
          id={`${kebabCase(label)}`}
          className="SustainabilityPageSlide__sticky-wrapper"
          style={{ zIndex: zIndex + 1 }}
        >
          <Small>
            <Image
              className="SustainabilityPageSlide__hero-image"
              src={get(heroImageMobile, ['file', 'url'])}
              alt={get(heroImageMobile, ['description'])}
              width={IMAGE_WIDTHS.W_2000}
              ratio={IMAGE_RATIOS.R_16x9}
            />
          </Small>
          <MediumUp>
            <Image
              className="SustainabilityPageSlide__hero-image"
              src={get(heroImageDesktop, ['file', 'url'])}
              alt={get(heroImageDesktop, ['description'])}
              width={IMAGE_WIDTHS.W_2000}
              ratio={IMAGE_RATIOS.R_16x9}
            />
          </MediumUp>
          <Container>
            <Grid isCentered>
              <Cell smallUp={{ columns: 12 }} mediumUp={{ columns: 11 }} largeUp={{ columns: 8 }}>
                <PrimaryHeading>{get(data, ['heading'])}</PrimaryHeading>
              </Cell>
            </Grid>
          </Container>
        </div>

        <div className="SustainabilityPageSlide__body" style={{ zIndex }}>
          <Container>
            <Grid isCentered>
              <Cell
                className="SustainabilityPageSlide__overview"
                smallUp={{ columns: 12 }}
                mediumUp={{ columns: 11 }}
                largeUp={{ columns: 8 }}
              >
                <RichText content={get(data, 'overview')} />
              </Cell>
            </Grid>
          </Container>
          {map(get(data, 'pageSections'), (_data, _i) => (
            <div key={_i} className="SustainabilityPageSlide__page-section">
              {this.renderPageSection(_data)}
            </div>
          ))}
        </div>
        <style jsx>
          {`
            .SustainabilityPageSlide__sticky-wrapper {
              width: 100%;
              height: 170px;
              position: relative;
              background-color: ${Theme.get('colors-grey')};
              overflow: hidden;
            }

            @media ${mediumUp} {
              .SustainabilityPageSlide__sticky-wrapper {
                display: inline-block;
                height: 231px;
                overflow: hidden;
              }
            }

            .SustainabilityPageSlide__body {
              padding: ${Theme.get('spacing') * 8}px 0;
              position: relative;
              background-color: ${Theme.get('colors-white')};
              min-height: 100vh;
              border-radius: 0 0 ${BORDER_RADIUS}px ${BORDER_RADIUS}px;
            }

            @media ${largeUp} {
              .SustainabilityPageSlide__body {
                padding: ${Theme.get('spacing') * 14}px 0;
              }
            }
          `}
        </style>
        <style jsx global>
          {`
            .SustainabilityPageSlide__hero-image {
              display: block;
              width: 100%;
              height: 100%;
              object-fit: cover;
            }

            .SustainabilityPageSlide__sticky-wrapper .Container {
              position: absolute;
              top: 0;
              bottom: 0;
              left: 0;
              right: 0;
            }

            .SustainabilityPageSlide__sticky-wrapper .Grid {
              height: 100%;
            }

            .SustainabilityPageSlide__sticky-wrapper .Grid__cell {
              display: flex;
              flex-direction: column;
              justify-content: flex-end;
            }

            .SustainabilityPageSlide__sticky-wrapper .typography--primary-heading {
              margin-bottom: 65px;
            }

            @media ${mediumUp} {
              .SustainabilityPageSlide__sticky-wrapper .typography--primary-heading {
                margin-bottom: ${Theme.get('spacing') * 10}px;
              }
            }

            @media ${largeUp} {
              .SustainabilityPageSlide__sticky-wrapper .typography--primary-heading {
                margin-bottom: ${Theme.get('spacing') * 14}px;
              }
            }

            .SustainabilityPageSlide__overview .Paragraph,
            .SustainabilityPageSlide__overview .InlineLink {
              font-size: 18px;
              font-weight: bold;
            }

            @media ${mediumUp} {
              .SustainabilityPageSlide__overview .Paragraph,
              .SustainabilityPageSlide__overview .InlineLink {
                font-size: 22px;
              }
            }

            .SustainabilityPageSlide__overview {
              padding-bottom: ${Theme.get('spacing') * 3}px;
            }

            @media ${mediumUp} {
              .SustainabilityPageSlide__overview {
                padding-bottom: ${Theme.get('spacing') * 5}px;
              }
            }

            @media ${largeUp} {
              .SustainabilityPageSlide__overview {
                padding-bottom: ${Theme.get('spacing') * 11}px;
              }

              .SustainabilityPageSlide__page-content {
                justify-content: flex-end;
              }
            }

            .SustainabilityPageSlide__page-section__card-carousel {
              overflow: hidden;
            }

            .SustainabilityPageSlide__page-section__card-carousel > .Container {
              padding: 0;
            }
            .SustainabilityPageSlide__page-section__card-carousel .CardCarousel__heading-section {
              padding: 0;
            }

            .SustainabilityPageSlide__page-section__card-carousel
              .HomepageV2Carousel__carousel-wrapper {
              padding: 0 ${Theme.get('spacing') * 7}px 0 ${Theme.get('spacing') * 4}px;
            }

            .SustainabilityPageSlide__page-section__card-carousel .HomepageV2Carousel > .Container {
              padding: 0;
            }

            @media ${mediumUp} {
              .SustainabilityPageSlide__page-section__card-carousel .HomepageV2Carousel {
                overflow: visible;
              }
            }

            @media ${largeUp} {
              .SustainabilityPageSlide__page-section__card-carousel .CardCarousel > .Container {
                padding-left: ${Theme.get('spacing') * 3}px;
              }

              .SustainabilityPageSlide__page-section__card-carousel
                .HomepageV2Carousel
                > .Container {
                padding: 0;
              }

              .SustainabilityPageSlide__page-section__card-carousel
                .HomepageV2Carousel__carousel-wrapper {
                padding: 0 ${Theme.get('spacing') * 10}px 0 ${Theme.get('spacing') * 3}px;
              }

              .SustainabilityPageSlide__page-section__card-carousel--overlay {
                position: absolute;
                height: 100%;
                width: 100%;
                top: 0;
                background: linear-gradient(
                  to right,
                  rgba(255, 255, 255, 1) 10%,
                  rgba(255, 255, 255, 0) 17%
                );
                pointer-events: none;
              }
            }
          `}
        </style>
      </>
    );
  }
}

SustainabilityPageSlide.propTypes = {
  data: PropTypes.shape({}),
  trackEvent: PropTypes.func,
  zIndex: PropTypes.number.isRequired,
  forwardedRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.elementType }),
  ]),
};

SustainabilityPageSlide.defaultProps = {
  data: {},
  trackEvent: () => {},
  forwardedRef: null,
};

export default SustainabilityPageSlide;
